import { FilterIcon, PlusIcon, SearchIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent } from '../ui/collapsible';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export type RecordsListFilters = {
  searchQuery: string;
  status: 'active' | 'inactive';
  ownership: 'internal' | 'external' | undefined;
};

// oxlint-disable-next-line react/only-export-components
export const defaultFilters: RecordsListFilters = {
  searchQuery: '',
  status: 'active',
  ownership: undefined,
};

/** Whether any control differs from its default — a search query, or a status or ownership choice. */
// oxlint-disable-next-line react/only-export-components
export const hasActiveFilters = (filters: RecordsListFilters): boolean =>
  filters.status !== defaultFilters.status ||
  filters.ownership !== defaultFilters.ownership ||
  filters.searchQuery !== defaultFilters.searchQuery;

/**
 * The search and filter rows animate their height open and shut the way the labs list's category rows
 * do: `tw-animate-css` keyframes interpolate to the `--radix-collapsible-content-height` Radix
 * measures onto the content, with `overflow-hidden` clipping to the height in flight. The clipping box
 * is widened by the controls' 3px focus ring (negative margin, restored as padding) so the ring is not
 * cut off once a control is focused. The gap above each row is the panel's own padding rather than a
 * parent `gap`, so a closed panel takes no space and the height animation can collapse it fully.
 */
const PANEL_CLASS = cn(
  'mx-[-3px] mb-[-3px] overflow-hidden px-[3px] pt-2 pb-[3px]',
  'data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
  'motion-reduce:animate-none',
);

export type RecordsListControlsProps = {
  className?: string;
  initialShowFilters?: boolean;
  onFiltersChange: (filters: RecordsListFilters) => void;
  searchPlaceholder: string;
  title?: React.ReactNode;
  /**
   * `'sm'` (default) renders the title as a compact `<p>` label — appropriate when the toolbar
   * sits inside a panel that already owns the page heading (e.g. patient-admin-web).
   * `'lg'` renders the title as a top-level `<h1>` — appropriate when the toolbar IS the page
   * top (e.g. patient-web).
   */
  titleSize?: 'sm' | 'lg';
  onAdd?: () => void;
  addLabel?: string;
  /** Optional `data-testid` for the Add button. Callers set this to give browser tests a stable hook. */
  addTestId?: string;
};

export const RecordsListControls = ({
  className,
  initialShowFilters = false,
  onFiltersChange,
  searchPlaceholder,
  title,
  titleSize = 'sm',
  onAdd,
  addLabel,
  addTestId,
}: RecordsListControlsProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const [showFilters, setShowFilters] = useState(initialShowFilters);
  const [showSearch, setShowSearch] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateFilters = useCallback(
    (patch: Partial<RecordsListFilters>) => {
      const next = { ...filters, ...patch };
      setFilters(next);
      onFiltersChange(next);
    },
    [filters, onFiltersChange],
  );

  useEffect(() => {
    if (showSearch) inputRef.current?.focus();
  }, [showSearch]);

  // Closing the search row clears its query, whichever way it closes, so a hidden query never stays
  // applied to the list.
  const setSearchOpen = useCallback(
    (open: boolean) => {
      if (!open && filters.searchQuery.length > 0) {
        updateFilters({ searchQuery: '' });
      }
      setShowSearch(open);
    },
    [filters.searchQuery, updateFilters],
  );
  const toggleSearch = (): void => setSearchOpen(!showSearch);

  const filtersActive = hasActiveFilters(filters);

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex flex-row items-center gap-2">
        {title !== undefined &&
          (titleSize === 'lg' ? (
            <h1 className="min-w-0 flex-1 truncate text-2xl font-semibold">{title}</h1>
          ) : (
            <p className="mx-3 min-w-0 flex-1 truncate text-sm font-medium text-muted-foreground">{title}</p>
          ))}
        {filtersActive && (
          <Button variant="ghost" className="text-muted-foreground" onClick={() => updateFilters(defaultFilters)}>
            {t('resetToDefault')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className={cn('ml-auto', showSearch ? 'bg-accent' : undefined)}
          onClick={toggleSearch}
          aria-label={searchPlaceholder}
          aria-expanded={showSearch}
        >
          <SearchIcon className="text-muted-foreground" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', showFilters && 'bg-accent')}
          onClick={() => setShowFilters(!showFilters)}
          aria-label={t('filterRecords')}
          aria-expanded={showFilters}
        >
          <FilterIcon className="text-muted-foreground" />
          {filtersActive && (
            <span aria-hidden className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-primary" />
          )}
        </Button>
        {onAdd && (
          <Button variant="ghost" size="icon" onClick={onAdd} aria-label={addLabel ?? t('add')} data-testid={addTestId}>
            <PlusIcon className="text-muted-foreground" />
          </Button>
        )}
      </div>

      <Collapsible open={showSearch} onOpenChange={setSearchOpen}>
        <CollapsibleContent className={PANEL_CLASS} data-testid="records-search-panel">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              type="search"
              placeholder={searchPlaceholder}
              className="border-border pl-9 shadow-none"
              value={filters.searchQuery}
              onChange={(e) => updateFilters({ searchQuery: e.target.value })}
              aria-label={searchPlaceholder}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={showFilters} onOpenChange={setShowFilters}>
        <CollapsibleContent className={PANEL_CLASS} data-testid="records-filters-panel">
          <div className="flex flex-row gap-2">
            <Select
              value={filters.status}
              onValueChange={(v) => updateFilters({ status: v as RecordsListFilters['status'] })}
            >
              <SelectTrigger size="sm" className="flex-1" aria-label={t('status')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t('active')}</SelectItem>
                <SelectItem value="inactive">{t('inactive')}</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.ownership ?? 'all'}
              onValueChange={(v) =>
                updateFilters({ ownership: v === 'all' ? undefined : (v as 'internal' | 'external') })
              }
            >
              <SelectTrigger size="sm" className="flex-1" aria-label={t('source')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="internal">{t('internal')}</SelectItem>
                <SelectItem value="external">{t('external')}</SelectItem>
                <SelectItem value="all">{t('all')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};
